import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Sparkles, Save, Check, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BrandSettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    company_name: '',
    logo_url: '',
    primary_color: '#1A1B2E',
    accent_color: '#D4AF37',
    custom_footer_text: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.plan === 'enterprise') {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/brand`, { withCredentials: true });
      if (data) {
        setSettings({
          company_name: data.company_name || '',
          logo_url: data.logo_url || '',
          primary_color: data.primary_color || '#1A1B2E',
          accent_color: data.accent_color || '#D4AF37',
          custom_footer_text: data.custom_footer_text || '',
        });
      }
    } catch (err) {
      setError('Failed to load brand settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/settings/brand`, settings, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Sparkles className="w-8 h-8 text-champagne animate-pulse" />
      </div>
    );
  }

  if (user?.plan !== 'enterprise') {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center p-4">
        <div className="aura-card rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-champagne mx-auto mb-4" />
          <h2 className="text-xl font-serif font-medium text-text-light mb-2">Enterprise Feature</h2>
          <p className="text-text-muted mb-6">White-label branding is available on the Enterprise plan.</p>
          <Link to="/pricing" className="btn-champagne inline-block">View Plans</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10 px-4 sm:px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-champagne" />
            <span className="font-serif text-text-light font-medium">Brand Settings</span>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-champagne flex items-center gap-2 text-sm px-4 py-2"
          >
            {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">{error}</div>
        )}

        {/* Company Identity */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="aura-card rounded-2xl p-6">
          <h2 className="text-lg font-serif font-medium text-text-light mb-4">Company Identity</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-text-muted block mb-2">Company Name</label>
              <input
                type="text"
                value={settings.company_name}
                onChange={e => handleChange('company_name', e.target.value)}
                placeholder="Acme Events"
                className="aura-input w-full"
              />
              <p className="text-xs text-text-muted mt-1">Shown in the portal header instead of "Memouraa"</p>
            </div>
            <div>
              <label className="text-sm text-text-muted block mb-2">Logo URL</label>
              <input
                type="url"
                value={settings.logo_url}
                onChange={e => handleChange('logo_url', e.target.value)}
                placeholder="https://yourcompany.com/logo.png"
                className="aura-input w-full"
              />
              {settings.logo_url && (
                <div className="mt-3 p-3 bg-midnight/50 rounded-xl inline-block">
                  <img src={settings.logo_url} alt="Logo preview" className="h-10 object-contain" onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-text-muted block mb-2">Custom Footer Text</label>
              <input
                type="text"
                value={settings.custom_footer_text}
                onChange={e => handleChange('custom_footer_text', e.target.value)}
                placeholder="Powered by Acme Events · All rights reserved"
                className="aura-input w-full"
              />
            </div>
          </div>
        </motion.div>

        {/* Brand Colors */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="aura-card rounded-2xl p-6">
          <h2 className="text-lg font-serif font-medium text-text-light mb-4">Brand Colors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-text-muted block mb-2">Primary Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.primary_color}
                  onChange={e => handleChange('primary_color', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border-0 bg-transparent"
                />
                <input
                  type="text"
                  value={settings.primary_color}
                  onChange={e => handleChange('primary_color', e.target.value)}
                  className="aura-input flex-1 font-mono text-sm"
                  placeholder="#1A1B2E"
                />
              </div>
              <p className="text-xs text-text-muted mt-1">Background / base color of portals</p>
            </div>
            <div>
              <label className="text-sm text-text-muted block mb-2">Accent Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.accent_color}
                  onChange={e => handleChange('accent_color', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border-0 bg-transparent"
                />
                <input
                  type="text"
                  value={settings.accent_color}
                  onChange={e => handleChange('accent_color', e.target.value)}
                  className="aura-input flex-1 font-mono text-sm"
                  placeholder="#D4AF37"
                />
              </div>
              <p className="text-xs text-text-muted mt-1">Button and highlight color</p>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6">
            <p className="text-sm text-text-muted mb-3">Preview</p>
            {(() => {
              const isHex = (c) => typeof c === 'string' && /^#[0-9A-Fa-f]{6}$/.test(c);
              const safePrimary = isHex(settings.primary_color) ? settings.primary_color : '#1A1B2E';
              const safeAccent = isHex(settings.accent_color) ? settings.accent_color : '#D4AF37';
              return (
            <div
              className="rounded-xl p-5 text-center"
              style={{ backgroundColor: safePrimary, border: `1px solid ${safeAccent}40` }}
            >
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="h-8 object-contain mx-auto mb-2" onError={e => { e.currentTarget.style.display='none'; }} />
              ) : (
                <p style={{ color: safeAccent }} className="text-lg font-bold tracking-widest">
                  {settings.company_name || 'YOUR BRAND'}
                </p>
              )}
              <p className="text-xs mt-2" style={{ color: safeAccent + 'aa' }}>
                {settings.custom_footer_text || 'Portal powered by your brand'}
              </p>
              <div className="mt-4">
                <button
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: safeAccent, color: safePrimary }}
                >
                  Upload Memory
                </button>
              </div>
            </div>
              );
            })()}
          </div>
        </motion.div>
      </div>
      <FooterExpanded />
    </div>
  );
}
